import { useCallback, useContext, useId } from 'react'
import { useSelector } from 'react-redux'
import { CheckCircleIcon, XCircleIcon, InformationCircleIcon } from "@heroicons/react/24/outline"
import { LabelList, PolarAngleAxis, RadialBar, RadialBarChart } from "recharts"
import { Tooltip } from 'react-tooltip'
import { ModalContext, ModalKey } from 'components/Modals/ModalContext'
import { WeekOfTheYearKey } from 'classes/WeekOfTheYearKey'
import { GetPrimaryColorHex } from 'redux/slices/brand'

const RADIAN = Math.PI / 180

function polarToCartesian(cx, cy, radius, angle) {
    return {
        x: cx + Math.cos(-RADIAN * angle) * radius,
        y: cy + Math.sin(-RADIAN * angle) * radius,
    }
}

function mathSign(n) {
    if (n === 0) return 0
    return n < 0 ? -1 : 1
}

function getDeltaAngle(startAngle, endAngle) {
    const sign = mathSign(endAngle - startAngle)
    return sign * Math.min(Math.abs(endAngle - startAngle), 360)
}

/**
 * Recharts polar `insideStart` labels use <textPath> along an arc (see recharts Label.js renderRadialLabel).
 * A custom `content` function replaces that entire branch — plain x/y are unset, so text was drawn at 0,0.
 * This mirrors the arc + textPath, with SVG stroke instead of text-shadow for Safari/iPad.
 *
 * WebKit / iPad notes (spec + engine issues, not device sniffing):
 * - MDN: SVG <textPath> `side` (`left` | `right`) chooses which side of the path glyphs sit on (SVG 2).
 *   https://developer.mozilla.org/en-US/docs/Web/SVG/Element/textPath
 * - WebKit has fixed several <textPath>/<tspan> layout regressions; remaining per-engine differences are common.
 *   e.g. https://github.com/WebKit/WebKit/pull/21271
 * - Safari historically needed `xlink:href` on <textPath> (often paired with `href` for newer engines).
 *   https://stackoverflow.com/questions/51404939/svg-textpath-not-working-in-safari
 * - For baseline quirks, community workarounds often use small `dy` on <tspan> inside <textPath> instead of
 *   relying on `dominant-baseline` alone in Safari.
 *   https://stackoverflow.com/questions/35565063/svg-dominant-baseline-not-working-in-safari
 */
function ComplianceArcRingLabel({ pathIdPrefix, styleConfig, viewBox, position = 'insideStart', offset = 5, value, payload, index = 0 }) {
    const labelText = payload?.label ?? value
    if (!viewBox || labelText == null || labelText === '') return null

    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, clockWise } = viewBox
    const radius = (innerRadius + outerRadius) / 2
    const deltaAngle = getDeltaAngle(startAngle, endAngle)
    const sign = deltaAngle >= 0 ? 1 : -1

    let labelAngle
    let direction = clockWise
    if (position === 'insideStart') {
        labelAngle = startAngle + sign * offset
        direction = clockWise
    } else if (position === 'insideEnd') {
        labelAngle = endAngle - sign * offset
        direction = !clockWise
    } else if (position === 'end') {
        labelAngle = endAngle + sign * offset
        direction = clockWise
    } else {
        labelAngle = startAngle + sign * offset
    }

    direction = deltaAngle <= 0 ? direction : !direction
    const startPoint = polarToCartesian(cx, cy, radius, labelAngle)
    const endPoint = polarToCartesian(cx, cy, radius, labelAngle + (direction ? 1 : -1) * 359)
    const path = `M${startPoint.x},${startPoint.y} A${radius},${radius},0,1,${direction ? 0 : 1}, ${endPoint.x},${endPoint.y}`

    const id = `${pathIdPrefix}-${index}`
    const outlineColor = GetPrimaryColorHex(styleConfig)

    return (
        <text
            dominantBaseline="central"
            fill="#ffffff"
            stroke={outlineColor}
            strokeWidth={1.75}
            strokeLinejoin="round"
            strokeLinecap="round"
            paintOrder="stroke fill"
            shapeRendering="geometricPrecision"
            className="recharts-radial-bar-label recharts-text"
            style={{
                fontSize: 14,
                fontWeight: 800,
                textTransform: 'capitalize',
            }}
        >
            <defs>
                <path id={id} d={path} />
            </defs>
            <textPath href={`#${id}`} xlinkHref={`#${id}`} side="right">
                {labelText}
            </textPath>
        </text>
    )
}

export default function ComplianceRadialDials({ 
    workoutCompliance = null,
    RADIAL_CHART_SIZE = 360,
    displayBottomStats = true
}) {
    const { ShowModal } = useContext(ModalContext)

    const styleConfig = useSelector((state) => state.brand.styleConfig)

    const strengthPercentage = workoutCompliance?.strength ?? 0
    const mobilityPercentage = workoutCompliance?.mobility ?? 0

    // Water level display variables

    var combinedCompletionPercentage = 0
    var waveY = 0

    if (workoutCompliance !== null) {
        combinedCompletionPercentage = Math.round((strengthPercentage + mobilityPercentage) / 2)
        waveY = combinedCompletionPercentage === 100 ? -4 : 6 // Show no wave if 100%
    }

    const GetCornerRadius = (percentage) => {
        if (percentage < 2) return 3
        if (percentage < 3) return 4
        if (percentage < 4) return 8
        if (percentage < 5) return 10
        return 12
    }

    const pathIdPrefix = useId().replace(/:/g, '')

    const renderStrengthRingLabel = useCallback(
        (props) => <ComplianceArcRingLabel {...props} pathIdPrefix={`${pathIdPrefix}-strength`} styleConfig={styleConfig} />,
        [pathIdPrefix, styleConfig],
    )

    const renderMobilityRingLabel = useCallback(
        (props) => <ComplianceArcRingLabel {...props} pathIdPrefix={`${pathIdPrefix}-mobility`} styleConfig={styleConfig} />,
        [pathIdPrefix, styleConfig],
    )

    const ShowIncompleteWorkoutsModal = (workoutType) => {

        // Generate current week and past 3 weeks (total 4 weeks)
        const weekKeys = Array.from({ length: 4 }, (_, i) => WeekOfTheYearKey.constructWithWeekOffset(-i)).map(key => key.toString())

        var modalData = null

        if (workoutType === 'strength') {
            modalData = {
                key: ModalKey.IncompleteAthleteRecords,
                size: 'third',
                titleText: 'Incomplete Strength Workouts',
                workoutType: 'strength',
                weekOfTheYearKeys: weekKeys
            }

            ShowModal(modalData)
        }

        if (workoutType === 'mobility') {
            modalData = {
                key: ModalKey.IncompleteAthleteRecords,
                size: 'third',
                titleText: 'Incomplete Mobility Workouts',
                workoutType: 'mobility',
                weekOfTheYearKeys: weekKeys
            }

            ShowModal(modalData)
        }

    }

    if (workoutCompliance === null) return null

    return (
        <>
            <div className="flex-1 w-full flex flex-col items-center justify-center relative">

                {/* Radial Fills */}
                <svg width={0} height={0}>
                    <defs>
                        <linearGradient id="strengthGradient" x1="0" y1="0" x2="1" y2="1">
                            <stop offset="0%" stopColor={GetPrimaryColorHex(styleConfig)} stopOpacity={1} />
                            <stop offset="100%" stopColor={GetPrimaryColorHex(styleConfig)} stopOpacity={1} />
                        </linearGradient>

                        <linearGradient id="mobilityGradient" x1="0" y1="0" x2="1" y2="1">
                            <stop offset="0%" stopColor={GetPrimaryColorHex(styleConfig)} stopOpacity={1} />
                            <stop offset="100%" stopColor={GetPrimaryColorHex(styleConfig)} stopOpacity={1} />
                        </linearGradient>
                    </defs>
                </svg>

                {/* Outer ring - Strength */}
                <div
                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center"
                    style={{ width: RADIAL_CHART_SIZE + 'px', height: RADIAL_CHART_SIZE + 'px' }}
                >
                    <RadialBarChart
                        width={RADIAL_CHART_SIZE}
                        height={RADIAL_CHART_SIZE}
                        data={[{ percentage: strengthPercentage, label: 'Strength: ' + strengthPercentage + '%' }]}
                        startAngle={90}
                        endAngle={-270}
                        innerRadius={Math.round(RADIAL_CHART_SIZE * 0.33)} // 101
                        outerRadius={Math.round(RADIAL_CHART_SIZE * 0.42)} // 132
                    >
                        <PolarAngleAxis
                            type="number"
                            domain={[0, 100]}
                            dataKey="percentage"
                            tick={false}
                        />

                        <RadialBar
                            dataKey="percentage"
                            background={{ fill: "#e5e7eb" }}
                            cornerRadius={GetCornerRadius(strengthPercentage)}
                            fill="url(#strengthGradient)"
                        >
                            <LabelList position="insideStart" dataKey="label" content={renderStrengthRingLabel} />
                        </RadialBar>
                    </RadialBarChart>
                </div>

                {/* Inner ring - Mobility */}
                <div
                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center"
                    style={{ width: RADIAL_CHART_SIZE + 'px', height: RADIAL_CHART_SIZE + 'px' }}
                >
                    <RadialBarChart
                        width={RADIAL_CHART_SIZE}
                        height={RADIAL_CHART_SIZE}
                        data={[{ percentage: mobilityPercentage, label: 'Mobility: ' + mobilityPercentage + '%' }]}
                        startAngle={90}
                        endAngle={-270}
                        innerRadius={Math.round(RADIAL_CHART_SIZE * 0.24)} // 67
                        outerRadius={Math.round(RADIAL_CHART_SIZE * 0.33)} // 96
                    >
                        <PolarAngleAxis
                            type="number"
                            domain={[0, 100]}
                            dataKey="percentage"
                            tick={false}
                        />

                        <RadialBar
                            dataKey="percentage"
                            background={{ fill: "#e5e7eb" }}
                            cornerRadius={GetCornerRadius(mobilityPercentage)}
                            fill="url(#mobilityGradient)"
                        >
                            <LabelList position="insideStart" dataKey="label" content={renderMobilityRingLabel} />
                        </RadialBar>
                    </RadialBarChart>
                </div>

                {/* Center water-level indicator and average text */}
                <div
                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center"
                    style={{ width: RADIAL_CHART_SIZE + 'px', height: RADIAL_CHART_SIZE + 'px' }}
                >
                    <div
                        className="absolute relative rounded-full"
                        style={{
                            width: Math.round(RADIAL_CHART_SIZE * 0.36) + 'px',
                            height: Math.round(RADIAL_CHART_SIZE * 0.36) + 'px',
                            overflow: 'hidden'
                        }}
                    >
                        {/* Water fill with static wave */}
                        <svg
                            className="absolute bottom-0 left-0 w-full"
                            viewBox="0 0 108 108"
                            preserveAspectRatio="none"
                            style={{
                                height: combinedCompletionPercentage > 0 ? `${Math.max(10, combinedCompletionPercentage)}%` : `${combinedCompletionPercentage}%`,
                                width: '100%'
                            }}
                        >
                            {/* Water surface line */}
                            <path
                                d={`M-27,${waveY} Q-13.5,${waveY - 6} 0,${waveY} T27,${waveY} T54,${waveY} T81,${waveY} T108,${waveY} T135,${waveY}`}
                                fill="none"
                                stroke={GetPrimaryColorHex(styleConfig, 2)}
                                strokeWidth="1.5"
                                vectorEffect="non-scaling-stroke"
                            />
                            
                            {/* Wavy water fill path */}
                            <path
                                d={`M-27,${waveY} Q-13.5,${waveY - 6} 0,${waveY} T27,${waveY} T54,${waveY} T81,${waveY} T108,${waveY} T135,${waveY} L135,108 L-27,108 Z`}
                                fill={GetPrimaryColorHex(styleConfig, -2)}
                            />
                        </svg>

                        {/* Text centered in circle */}
                        <div
                            className="absolute left-2 right-0 flex items-center justify-center"
                            style={{
                                top: '50%',
                                transform: 'translateY(-50%)'
                            }}
                        >
                            <div className="text-orange-400">
                                <span 
                                    className="font-bold text-4xl" 
                                    style={{ 
                                        textShadow: '-1px -1px 0 white, 1px -1px 0 white, -1px 1px 0 white, 1px 1px 0 white, 0 -1px 0 white, 0 1px 0 white, -1px 0 0 white, 1px 0 0 white',
                                        WebkitTextStroke: '2px white',
                                        paintOrder: 'stroke fill'
                                    }}
                                >
                                    {combinedCompletionPercentage}
                                </span>

                                <span 
                                    className="font-bold text-2xl" 
                                    style={{ 
                                        textShadow: '-1px -1px 0 white, 1px -1px 0 white, -1px 1px 0 white, 1px 1px 0 white, 0 -1px 0 white, 0 1px 0 white, -1px 0 0 white, 1px 0 0 white',
                                    }}
                                >
                                    %
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {displayBottomStats && (
                <div className="w-full px-6 py-4 space-y-3 bg-gray-100 border-t border-gray-200">
                    <div
                        className="flex items-center"
                        // onClick={() => ShowIncompleteWorkoutsModal('strength')}
                    >
                        <InformationCircleIcon
                            className="h-4 w-4 text-gray-600 mr-1"
                            data-tooltip-id="strength-missed-tooltip"
                            data-tooltip-content="Strength compliance over the past 4 weeks"
                        />

                        <Tooltip
                            id="strength-missed-tooltip"
                            style={{ maxWidth: '180px', fontSize: '12px', whiteSpace: 'normal', wordWrap: 'break-word' }}
                        />

                        <span className="text-sm font-medium flex-grow">Strength</span>

                        <CheckCircleIcon className="h-4 w-4 text-emerald-500 mr-1" />

                        <span className="text-sm w-10 mr-1">{strengthPercentage}%</span>

                        <XCircleIcon className="h-4 w-4 text-rose-500 mr-1" />

                        <span className="text-sm w-10">{100 - strengthPercentage}%</span>
                    </div>

                    <div
                        className="flex items-center"
                        // onClick={() => ShowIncompleteWorkoutsModal('mobility')}
                    >
                        <InformationCircleIcon
                            className="h-4 w-4 text-gray-600 mr-1"
                            data-tooltip-id="mobility-missed-tooltip"
                            data-tooltip-content="Mobility compliance over the past 4 weeks"
                        />

                        <Tooltip
                            id="mobility-missed-tooltip"
                            style={{ maxWidth: '180px', fontSize: '12px', whiteSpace: 'normal', wordWrap: 'break-word' }}
                        />

                        <span className="text-sm font-medium flex-grow">Mobility</span>

                        <CheckCircleIcon className="h-4 w-4 text-emerald-500 mr-1" />

                        <span className="text-sm w-10 mr-1">{mobilityPercentage}%</span>

                        <XCircleIcon className="h-4 w-4 text-rose-500 mr-1" />

                        <span className="text-sm w-10">{100 - mobilityPercentage}%</span>
                    </div>
                </div>
            )}
        </>
    )
}